/**
 * Maps a BE error_code from the /api/adjustmentsOfData/* namespace to a
 * frontend i18n key under `dataAdjustments.backendErrors.*`.
 *
 * Used by both Datenanpassung (W8) and Anomalieerkennung (W9) since they
 * share the BE namespace. Mirrors the W6 pattern from
 * features/data-upload/utils/backendErrorMapper.ts.
 *
 * Contract from BE (post-T3): localized text lives in `suggestions[0]`,
 * `message` field is auto-generated English. The mapper accepts both —
 * `suggestion` parameter (taken from `suggestions[0]`) wins over `message`
 * for the fallback when translation key is null, so unknown codes still
 * display the localized text the BE sent.
 *
 * Returns null translationKey for unknown codes so callers can fall back
 * to suggestion (preferred) or message.
 */

export const KNOWN_ADJUSTMENTS_BACKEND_ERROR_CODES = [
  // LoadDataException codes (shared with W6 inheritance)
  'VALIDATION_ERROR',
  'MISSING_PARAMETER',
  'INVALID_PARAMETER',
  'DELIMITER_MISMATCH',
  'INVALID_COLUMN_INDEX',
  'PARSING_ERROR',
  'CSV_PARSING_ERROR',
  'DATETIME_PARSING_ERROR',
  'ENCODING_ERROR',
  'UPLOAD_ERROR',
  'CHUNK_UPLOAD_ERROR',
  'UPLOAD_NOT_FOUND',
  'INCOMPLETE_UPLOAD',
  'TIMEZONE_ERROR',
  'UNSUPPORTED_TIMEZONE',
  'TIMEZONE_CONVERSION_ERROR',
  'LOAD_DATA_ERROR',
  // Anomaly-specific codes from T3
  'ANOMALY_ERROR',
  'STL_PERIOD_NOT_ALIGNED',
  'LSTM_HYPERPARAM_OUT_OF_RANGE',
  'SBAD_SLOPE_REQUIRES_BOTH',
  'THRESHOLD_OUT_OF_RANGE',
  'PARAM_OUT_OF_RANGE',
  'PARAM_NOT_FLOAT',
  'PARAM_NOT_INTEGER',
  'PARAM_COMPARISON_FAILED',
  'STL_PERIOD_REQUIRED',
  'STL_REQUIRES_DT_AVG',
  'LSTM_PERIOD_REQUIRED',
  'UNKNOWN_PARAMETER',
  'PARAM_NOT_BOOLEAN',
  'NO_VALIDATION_RULE',
  'TIME_GRID_REQUIRED',  // BE may emit this when CSV timestamps are not evenly spaced
] as const;

export type AdjustmentsBackendErrorCode =
  (typeof KNOWN_ADJUSTMENTS_BACKEND_ERROR_CODES)[number];

export interface AdjustmentsBackendErrorInput {
  code: string | undefined;
  message: string | undefined;
  /** Preferred over `message` for fallback since it's localized by BE. Pass `suggestions[0]` here. */
  suggestion?: string | undefined;
}

export interface AdjustmentsBackendErrorMapping {
  translationKey: string | null;
  fallbackMessage: string;
}

const KNOWN_SET = new Set<string>(KNOWN_ADJUSTMENTS_BACKEND_ERROR_CODES);

export function mapAdjustmentsBackendError(
  input: AdjustmentsBackendErrorInput,
): AdjustmentsBackendErrorMapping {
  // Prefer localized suggestion over auto-English message for fallback.
  const fallbackMessage = input.suggestion ?? input.message ?? '';
  if (input.code && KNOWN_SET.has(input.code)) {
    return {
      translationKey: `backendErrors.${input.code}`,
      fallbackMessage,
    };
  }
  return { translationKey: null, fallbackMessage };
}
